<script setup lang="ts">
import useVuelidate from "@vuelidate/core";
import { useRouter } from "vue-router";
import axios from "axios";
import { computed, onMounted, reactive } from "vue";
import { captureException, setTag } from "@sentry/vue";
import z from "zod";

import Question from "./fields/Question.vue";
import Input from "./fields/Input.vue";
import { applicationCompleteAndSendValidator } from "@/utils/validators";
import { isNumber } from "@/utils";
import { tooltips } from "@/tooltips/applicationTooltips";
import Loader from "@/components/Loader.vue";
import Modal from "@/components/Modal.vue";
import { useQuoteStore } from "@/stores/quotes";
import { scrollErrorIntoView } from "@/composables/scrollErrorIntoView";
import {
    useApplicationStore,
    RequestStatus,
    ApplicationStatus,
} from "@/stores/application";
import { submitApplicationData } from "@/data/apply";
import { LINKS } from "@/constants/links";

const applicationStore = useApplicationStore();
const quoteStore = useQuoteStore();
const v$ = useVuelidate();
const router = useRouter();

const validator = applicationCompleteAndSendValidator(applicationStore);

const state = reactive({
    contactAddressLine1Disabled: false,
    contactAddressLine2Disabled: false,
    contactPostTownDisabled: false,
    contactPostcodeDisabled: false,
    bankDisabled: false,
    branchDisabled: false,
});

onMounted(() => {
    if (applicationStore.paymentInformation.accountNumber &&
        applicationStore.paymentInformation.sortCode &&
        !applicationStore.isManualBankEntry) {
        fetchSingleBank();
    }
});

function resetBankForm() {
    applicationStore.paymentInformation.address.line1 = "";
    applicationStore.paymentInformation.address.line2 = "";
    applicationStore.paymentInformation.address.city = "";
    applicationStore.paymentInformation.address.postcode = "";
    applicationStore.paymentInformation.bankName = "";
    applicationStore.paymentInformation.bankBranchName = "";
    state.contactAddressLine1Disabled = false;
    state.contactAddressLine2Disabled = false;
    state.contactPostTownDisabled = false;
    state.contactPostcodeDisabled = false;
    state.bankDisabled = false;
    state.branchDisabled = false;
}

const getBank = async () => {
    const response = await axios.post("/bank-account-validation", {
        accountNumber: applicationStore.paymentInformation.accountNumber,
        sortCode: applicationStore.paymentInformation.sortCode,
    }, {
        headers: {
            "Content-Type": "application/json",
        },
    });
    const maybeResponse = z.array(bank)
        .or(z.array(bankError))
        .parse(response.data.Items);

    if ("Error" in maybeResponse[0]) {
        setTag("quoteNumber", applicationStore.quoteNumber);
        captureException(new Error(JSON.stringify(maybeResponse[0])));
        return;
    }

    return maybeResponse[0];
};

const bank = z.object({
    ContactAddressLine1: z.string(),
    ContactAddressLine2: z.string(),
    ContactPostTown: z.string(),
    ContactPostcode: z.string(),
    Bank: z.string(),
    Branch: z.string(),
    IsCorrect: z.boolean(),
});

const bankError = z.object({
    Error: z.string(),
    Description: z.string(),
    Cause: z.string(),
    Resolution: z.string(),
});

type Bank = z.infer<typeof bank>;

const setBankData = (bank: Bank) => {
    if (bank.ContactAddressLine1) {
        applicationStore.paymentInformation.address.line1 = bank.ContactAddressLine1;
        state.contactAddressLine1Disabled = true;
    }
    if (bank.ContactAddressLine2) {
        applicationStore.paymentInformation.address.line2 = bank.ContactAddressLine2;
        state.contactAddressLine2Disabled = true;
    }
    if (bank.ContactPostTown) {
        applicationStore.paymentInformation.address.city = bank.ContactPostTown;
        state.contactPostTownDisabled = true;
    }
    if (bank.ContactPostcode) {
        applicationStore.paymentInformation.address.postcode = bank.ContactPostcode;
        state.contactPostcodeDisabled = true;
    }
    if (bank.Bank) {
        applicationStore.paymentInformation.bankName = bank.Bank;
        state.bankDisabled = true;
    }
    if (bank.Branch) {
        applicationStore.paymentInformation.bankBranchName = bank.Branch;
        state.branchDisabled = true;
    }
};

const fetchSingleBank = async () => {
    if (applicationStore.paymentInformation.accountNumber === null) {
        return;
    }

    try {
        const bank = await getBank();
        resetBankForm();
        if (bank && bank.IsCorrect) {
            setBankData(bank);
            applicationStore.isManualBankEntry = false;
        } else {
            applicationStore.isManualBankEntry = true;
        }
    } catch (err) {
        captureException(err);
    }
};

const submitApplication = async () => {
    const isValid = await v$.value.$validate();

    if (!isValid) {
        scrollErrorIntoView();
        return;
    }

    try {
        const applicationResult = await submitApplicationData(
            applicationStore.quoteNumber,
            quoteStore.latestTokenByQuoteNumber(applicationStore.quoteNumber).token,
            applicationStore.premiumId,
        );
        applicationStore.applicationId = applicationResult.applicationId;
        applicationStore.applicationStatus = ApplicationStatus.GeneratingPack;
        router.push({
            path: `/apply/${applicationStore.applicationStage + 1}`,
            query: {
                premiumId: applicationStore.premiumId,
                quoteNumber: applicationStore.quoteNumber,
            },
        });
    } catch (err) {
        applicationStore.applicationRequestStatus = RequestStatus.Failed;
    }
};

const isLandG = computed(() => applicationStore.companySlug === "lg");

const isPolling = computed(() => applicationStore.isPolling);

</script>
<template>
    <div class="row">
        <div class="col-12 mt-1">
            <div class="card">
                <h4 class="font-weight-bold-col">
                    Your payments
                </h4>
                <p
                    v-if="isLandG"
                    class="pb-3"
                >
                    Please provide details of the account you would like your annuity income and tax-free lump sum paid into. Payments must be into an account of which you are either the sole or joint account holder.
                </p>
                <p
                    v-else
                    class="pb-3"
                >
                    When you return your signed application, please provide details of the account you would like your annuity income and tax-free lump sum to be paid into. Payments must be into an account of which you are either the sole or joint account holder.
                </p>
                <p class="alert alert-info">
                    Any tax due will normally be deducted by your annuity provider before you receive each payment. All payments will be made to you in British Pounds Sterling. Any conversion to another currency will be at your own expense. For your security Annuity Ready will not retain your bank account details longer than is necessary to ensure your annuity is in payment.
                </p>
            </div>
            <div
                v-if="isLandG"
                id="bankForm"
            >
                <Question
                    name="accountNumber"
                    label="Account number"
                    input-class="col-md-8"
                    :tooltip="tooltips.accountNumber"
                    label-class="order-first col-md-2 col-form-label pt-0"
                >
                    <Input
                        v-model="applicationStore.paymentInformation.accountNumber"
                        placeholder="Account number"
                        inputmode="numeric"
                        type="number"
                        class="col-md-8"
                        :validation="validator.accountNumber"
                        :filter="isNumber"
                    />
                </Question>
                <Question
                    name="sortCode"
                    label="Sort code"
                    input-class="col-md-8"
                    :tooltip="tooltips.sortCode"
                    label-class="order-first col-md-2 col-form-label pt-0"
                >
                    <Input
                        v-model="applicationStore.paymentInformation.sortCode"
                        placeholder="Sort code"
                        inputmode="text"
                        type="text"
                        class="col-md-5"
                        :validation="validator.sortCode"
                        :emit-blur-event="true"
                        @blur="fetchSingleBank"
                    />
                </Question>
                <Question
                    name="bankName"
                    label="Bank name"
                    input-class="col-md-8"
                    label-class="order-first col-md-2 col-form-label pt-0"
                >
                    <Input
                        v-model="applicationStore.paymentInformation.bankName"
                        placeholder="Bank name"
                        inputmode="text"
                        type="text"
                        class="col-md-8"
                        :validation="validator.bankName"
                        :disabled="state.bankDisabled"
                    />
                </Question>
                <Question
                    name="bankBranchName"
                    label="Bank branch name"
                    input-class="col-md-8"
                    label-class="order-first col-md-2 col-form-label pt-0"
                >
                    <Input
                        v-model="applicationStore.paymentInformation.bankBranchName"
                        placeholder="Bank branch name"
                        inputmode="text"
                        type="text"
                        class="col-md-8"
                        :validation="validator.bankBranchName"
                        :disabled="state.branchDisabled"
                    />
                </Question>
                <Question
                    name="bankAddress1"
                    label="Bank address 1"
                    input-class="col-md-8"
                    label-class="order-first col-md-2 col-form-label pt-0"
                >
                    <Input
                        v-model="applicationStore.paymentInformation.address.line1"
                        placeholder="Bank address 1"
                        inputmode="text"
                        type="text"
                        class="col-md-8"
                        :validation="validator.line1"
                        :disabled="state.contactAddressLine1Disabled"
                    />
                </Question>
                <Question
                    name="bankAddress2"
                    label="Bank address 2"
                    input-class="col-md-8"
                    label-class="order-first col-md-2 col-form-label pt-0"
                >
                    <Input
                        v-model="applicationStore.paymentInformation.address.line2"
                        placeholder="Bank address 2"
                        inputmode="text"
                        type="text"
                        class="col-md-8"
                        :validation="validator.line2"
                        :disabled="state.contactAddressLine2Disabled"
                    />
                </Question>
                <Question
                    name="bankAddressCity"
                    label="Bank city"
                    input-class="col-md-8"
                    label-class="order-first col-md-2 col-form-label pt-0"
                >
                    <Input
                        v-model="applicationStore.paymentInformation.address.city"
                        placeholder="Bank city"
                        inputmode="text"
                        type="text"
                        class="col-md-8"
                        :validation="validator.city"
                        :disabled="state.contactPostTownDisabled"
                    />
                </Question>
                <Question
                    name="bankPostCode"
                    label="Bank postcode"
                    input-class="col-md-8"
                    label-class="order-first col-md-2 col-form-label pt-0"
                >
                    <Input
                        v-model="applicationStore.paymentInformation.address.postcode"
                        placeholder="Bank postcode"
                        inputmode="text"
                        type="text"
                        class="col-md-8"
                        :validation="validator.postcode"
                        :disabled="state.contactPostcodeDisabled"
                    />
                </Question>
                <Question
                    name="accountHolderName"
                    label="Account holder's name"
                    input-class="col-md-8"
                    label-class="order-first col-md-2 col-form-label pt-0"
                >
                    <Input
                        v-model="applicationStore.paymentInformation.accountHolder"
                        placeholder="Account holder's name"
                        inputmode="text"
                        type="text"
                        class="col-md-8"
                        :validation="validator.accountHolder"
                    />
                </Question>
                <Question
                    name="buildingSociety"
                    label="Building society roll number (if applicable)"
                    input-class="col-md-8"
                    label-class="order-first col-md-2 col-form-label pt-0"
                >
                    <Input
                        v-model.number="applicationStore.paymentInformation.buildingSociety"
                        placeholder="Building society roll number"
                        inputmode="text"
                        type="text"
                        class="col-md-8"
                    />
                </Question>
            </div>
            <div id="declaration">
                <hr>
                <h4 class="font-weight-bold-col">
                    Declaration
                </h4>
                <p class="alert alert-info">
                    Please remember that it is a serious offence to make false statements; the penalties are severe and could lead to prosecution.
                </p>
                <p>
                    I confirm that to the best of my knowledge my answers are full and accurate, and I have read and understood the provider documents including the quote (key facts), key features and terms &amp; conditions.
                    <br><br>
                    I agree that my chosen quote and the information contained in this application, plus any medical and/or lifestyle information provided separately, will be used to determine the income I am to be paid. If any of the information is found to be incorrect my income may be adjusted accordingly.
                    <br><br>
                    I understand that benefits under this policy cannot be transferred to anyone else or surrendered except as provided in the relevant legislation and subject to the agreement of {{ applicationStore.companyName }}.
                    <br><br>
                    If I have been contracted-out under my current plan, then I agree to {{ applicationStore.companyName }} calculating the part of the transfer payment to be treated as relating to contracted-out benefits, if this information is not provided.
                    <br><br>
                    If the total gross contribution(s) paid into my Registered Pension Scheme(s) exceed the Standard Annual Allowance (£60,000 for tax year 2023/2024), or the Money Purchase Annual Allowance if it applies to me (£10,000 for tax year 2023/2024), I will inform HM Revenue &amp; Customs. I agree that where a tax charge does arise, any of the funds used to purchase this annuity will not be available to pay that charge.
                    <br><br>
                </p>
            </div>
        </div>
        <div class="mb-4">
            By clicking ‘Process application’, I/we agree to Annuity Ready processing the information including medical, health, lifestyle and bank details that I/we have provided, in accordance with the Annuity Ready
            <a
                :href="LINKS.ANNUITY_TERMS_AND_CONDITIONS"
                target="_blank"
                rel="noopener noreferrer"
            >terms &amp; conditions</a>
            and
            <a
                :href="LINKS.ANNUITY_PRIVACY_POLICY"
                target="_blank"
                rel="noopener noreferrer"
            >privacy notice</a>
            and that Annuity Ready can pass these details to my/our selected annuity provider for the purposes of underwriting, maintaining and servicing the annuity contract.
        </div>
        <button
            id="process_application_button"
            type="button"
            class="mb-3 btn btn-forward font-weight-bold mt-3"
            :class="[isPolling ? 'disabled' : '']"
            :disabled="isPolling"
            @click="submitApplication"
        >
            <span>Process application</span>
        </button>
        <Modal
            id="application-error"
            open-focus="close-application-error"
            close-focus="process_application_button"
            header="Sorry, there's been an error"
            :show="applicationStore.applicationRequestStatus === RequestStatus.Failed"
            @close="applicationStore.applicationRequestStatus = RequestStatus.Idle"
        >
            <template #body>
                <p>
                    An application pack can be printed and posted to you instead.
                    <br><br>
                    Please contact our customer service team on <a href="tel:08003265479">0800 326 5479</a>. to request your application pack.
                </p>
            </template>
            <template #footer>
                <button
                    id="retry-application"
                    type="button"
                    class="btn btn-secondary"
                    @click.prevent="submitApplication"
                >
                    Retry
                </button>
            </template>
        </Modal>
        <Loader v-show="applicationStore.applicationRequestStatus === RequestStatus.Pending" />
    </div>
</template>
